import * as React from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import EventIcon from '@material-ui/icons/Event'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'

import '../date-picker-multi-select-new/index.scss'
import { DEFAULT_CALENDAR_HEIGHT, getCalendarPositionStyles } from '../date-picker-multi-select-new/calendarPosition'
import StyledInput from '../../../civic-champs-shared/core/StyledInput'

export interface SingleDatePickerFieldProps {
  label: string
  placeholder?: string
  value?: Date | null
  onChange: (date: Date | null) => void
  minDate?: Date
  openToDate?: Date
  error?: string
  disabled?: boolean
  inputVariant?: 'filter' | 'form'
}

const useStyles = makeStyles({
  adornment: {
    right: '25px',
  },
})

const SingleDatePickerField = ({
  label,
  placeholder = 'MM/DD/YYYY',
  value,
  onChange,
  minDate,
  openToDate,
  error,
  disabled,
  inputVariant = 'form',
}: SingleDatePickerFieldProps) => {
  const classes = useStyles()
  const [showDatepicker, setShowDatepicker] = useState(false)
  const [calendarStyles, setCalendarStyles] = useState<React.CSSProperties>({})
  const inputRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  const updateCalendarPosition = useCallback(() => {
    if (!showDatepicker || !inputRef.current) {
      setCalendarStyles({})
      return
    }

    const calendarHeight = calendarRef.current?.offsetHeight || DEFAULT_CALENDAR_HEIGHT
    setCalendarStyles(getCalendarPositionStyles(inputRef.current, calendarHeight))
  }, [showDatepicker])

  useLayoutEffect(() => {
    updateCalendarPosition()
  }, [updateCalendarPosition, showDatepicker])

  useEffect(() => {
    if (!showDatepicker) {
      return
    }

    window.addEventListener('resize', updateCalendarPosition)
    window.addEventListener('scroll', updateCalendarPosition, true)

    return () => {
      window.removeEventListener('resize', updateCalendarPosition)
      window.removeEventListener('scroll', updateCalendarPosition, true)
    }
  }, [showDatepicker, updateCalendarPosition])

  useEffect(() => {
    if (!showDatepicker) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (inputRef.current?.contains(target) || calendarRef.current?.contains(target)) {
        return
      }

      setShowDatepicker(false)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDatepicker])

  const handleChange = (date: Date | null) => {
    onChange(date)
    setShowDatepicker(false)
  }

  const displayValue = value ? moment(value).format('MM/DD/YYYY') : ''
  const calendarOpenToDate = useMemo(() => openToDate || value || new Date(), [openToDate, value])

  const calendar = (
    <div
      ref={calendarRef}
      className={cn(
        'custom-datepicker-multi-select-new__datepicker',
        'custom-datepicker-multi-select-new__datepicker--portal',
      )}
      style={calendarStyles}
    >
      <DatePicker
        inline
        disabled={disabled}
        selected={value || undefined}
        dateFormat="MM/dd/yyyy"
        minDate={minDate}
        showYearDropdown
        onChange={handleChange}
        openToDate={calendarOpenToDate}
      />
    </div>
  )

  return (
    <div className="custom-datepicker-multi-select-new">
      <StyledInput
        label={label}
        placeholder={placeholder}
        ref={inputRef}
        variant={inputVariant}
        onChange={() => {}}
        onFocus={() => !disabled && setShowDatepicker(true)}
        value={displayValue}
        endAdornment={<EventIcon />}
        endAdornmentClassName={classes.adornment}
        error={error}
        disabled={disabled}
      />
      {showDatepicker &&
        createPortal(
          <div className="custom-datepicker-multi-select-new custom-datepicker-multi-select-new--portal">
            {calendar}
          </div>,
          document.body,
        )}
    </div>
  )
}

export default SingleDatePickerField
